import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { BookingType } from '@classpass/protobuf-definitions/lib/web/models/shared/reservation_service/booking';
import { Card } from 'components/Card';
import When from 'components/When';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import { getReportRules, REPORTABLE_BOOKING_TYPES } from './ReservationFeedback.bookingTypes';
import ReasonSelection from './Steps/ReasonSelection';
import DisruptiveUserReportDetails from './Steps/DisruptiveUserReportDetails';
import ProcessingReport from './Steps/ProcessingReport';
import PaidSuccessReport from './Steps/PaidSuccessReport';
import UnpaidSuccessReport from './Steps/UnpaidSuccessReport';
import TrialAbuseReportDetails from './Steps/TrialAbuseReportDetails';

import './ReservationFeedbackModalV2.scss';

const { block } = bem('reservation-feedback-modal-v2');

const STEPS = {
  REASON_SELECTION: 'REASON_SELECTION',
  DISRUPTIVE_USER_REPORT_DETAILS: 'DISRUPTIVE_USER_REPORT_DETAILS',
  TRIAL_ABUSE_REPORT_DETAILS: 'TRIAL_ABUSE_REPORT_DETAILS',
  PROCESSING: 'PROCESSING',
  SUCCESS: 'SUCCESS',
};

const ReservationFeedbackModalV2 = ({
  bookingType = BookingType.RESERVATION,
  disruptiveUserAlreadyReported,
  fullName,
  profileUrl,
  classEntityType,
  venueId,
  reservationId,
  orderId,
  resizeModal,
  onReported,
  trialAbuseAlreadyReported,
}) => {
  const reportRules = getReportRules(bookingType);
  const submitReport = ({ feedback, description }) =>
    reportRules.submit({ reservationId, orderId, feedback, description });
  const [currentStep, setCurrentStep] = React.useState(STEPS.REASON_SELECTION);
  const [isPaid, setIsPaid] = React.useState(true);
  const [failedFormValues, setFailedFormValues] = React.useState(null);

  useEffect(() => {
    if (currentStep === STEPS.SUCCESS && venueId != null && reservationId != null) {
      Segment.event('Viewed Report Submitted', { VID: venueId, RID: reservationId });
    }
  }, [currentStep, venueId, reservationId]);

  const setDisruptiveUserReportDetailsStep = () => {
    setFailedFormValues(null);
    setCurrentStep(STEPS.DISRUPTIVE_USER_REPORT_DETAILS);
  };

  const setTrialAbuseReportDetailsStep = () => {
    setFailedFormValues(null);
    setCurrentStep(STEPS.TRIAL_ABUSE_REPORT_DETAILS);
  };

  const handleDisruptiveUserSubmitError = formValues => {
    setFailedFormValues(formValues);
    setCurrentStep(STEPS.DISRUPTIVE_USER_REPORT_DETAILS);
  };

  const handleTrialAbuseSubmitError = formValues => {
    setFailedFormValues(formValues);
    setCurrentStep(STEPS.TRIAL_ABUSE_REPORT_DETAILS);
  };

  const setProcessingStep = () => {
    setCurrentStep(STEPS.PROCESSING);
  };

  const setSuccessStep = paid => {
    setIsPaid(paid);
    setCurrentStep(STEPS.SUCCESS);
  };

  return (
    <Card className={block()} data-cypress="reservation-feedback-modal-v2-confirm">
      <When predicate={currentStep === STEPS.REASON_SELECTION}>
        <ReasonSelection
          fullName={fullName}
          profileUrl={profileUrl}
          venueId={venueId}
          reservationId={reservationId}
          shouldShowTrialAbuseReason={reportRules.offersTrialAbuse(classEntityType)}
          disruptiveUserDescription={reportRules.disruptiveUserDescription}
          setDisruptiveUserReportDetailsStep={setDisruptiveUserReportDetailsStep}
          setTrialAbuseReportDetailsStep={setTrialAbuseReportDetailsStep}
          disruptiveUserAlreadyReported={disruptiveUserAlreadyReported}
          trialAbuseAlreadyReported={trialAbuseAlreadyReported}
        />
      </When>
      <When predicate={currentStep === STEPS.DISRUPTIVE_USER_REPORT_DETAILS}>
        <DisruptiveUserReportDetails
          fullName={fullName}
          profileUrl={profileUrl}
          venueId={venueId}
          reservationId={reservationId}
          guidelines={reportRules.disruptiveUserGuidelines}
          disruptiveReasons={reportRules.disruptiveReasons}
          submitReport={submitReport}
          onReported={onReported}
          setProcessingStep={setProcessingStep}
          setSuccessStep={setSuccessStep}
          resizeModal={resizeModal}
          failedFormValues={failedFormValues}
          onSubmitError={handleDisruptiveUserSubmitError}
        />
      </When>
      <When predicate={currentStep === STEPS.TRIAL_ABUSE_REPORT_DETAILS}>
        <TrialAbuseReportDetails
          fullName={fullName}
          profileUrl={profileUrl}
          venueId={venueId}
          reservationId={reservationId}
          submitReport={submitReport}
          onReported={onReported}
          setProcessingStep={setProcessingStep}
          setSuccessStep={setSuccessStep}
          resizeModal={resizeModal}
          failedFormValues={failedFormValues}
          onSubmitError={handleTrialAbuseSubmitError}
        />
      </When>
      <When predicate={currentStep === STEPS.PROCESSING}>
        <ProcessingReport />
      </When>
      <When predicate={currentStep === STEPS.SUCCESS}>
        {isPaid ? (
          <PaidSuccessReport venueId={venueId} reservationId={reservationId} />
        ) : (
          <UnpaidSuccessReport venueId={venueId} reservationId={reservationId} />
        )}
      </When>
    </Card>
  );
};

ReservationFeedbackModalV2.propTypes = {
  bookingType: PropTypes.oneOf(REPORTABLE_BOOKING_TYPES),
  fullName: PropTypes.string,
  profileUrl: PropTypes.string,
  classEntityType: PropTypes.string,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  orderId: PropTypes.string,
  resizeModal: PropTypes.func,
  onReported: PropTypes.func,
  disruptiveUserAlreadyReported: PropTypes.bool,
  trialAbuseAlreadyReported: PropTypes.bool,
};

export default ReservationFeedbackModalV2;
